import {
  AsyncThunkConfig,
  AsyncThunkOptions,
  GetThunkAPI,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { ApiResponseError } from "./api";
import { errorToastShown } from "./app/error-toast";
import { handleActionError } from "../helpers/actionError";

/**
 * Drop-in replacement for createAsyncThunk for actions the driver triggers
 * directly (saving a weight, deleting a note, ...). On failure it reports the
 * error to Sentry as a real Error (network errors excepted) and shows the
 * global error toast, then rethrows so callers still see the action as
 * rejected (e.g. to keep a modal open).
 *
 * Callers that `.unwrap()` still need a (no-op) catch, otherwise the rejection
 * surfaces as an unhandled promise rejection.
 */
export const createErrorReportingAsyncThunk = <Returned, ThunkArg = void>(
  typePrefix: string,
  payloadCreator: (
    arg: ThunkArg,
    thunkApi: GetThunkAPI<AsyncThunkConfig>
  ) => Promise<Returned>,
  options?: AsyncThunkOptions<ThunkArg>
) =>
  createAsyncThunk<Returned, ThunkArg>(
    typePrefix,
    (arg, thunkApi) =>
      payloadCreator(arg, thunkApi).catch((e: unknown) => {
        // A 401 already logs the driver out (see ducks/api.ts); nothing to
        // report or show.
        if (!(e instanceof ApiResponseError && e.status === 401)) {
          const messageKey = handleActionError(e, {
            type: "Async action failed",
            actionType: typePrefix,
          });
          thunkApi.dispatch(errorToastShown(messageKey));
        }
        throw e;
      }),
    options
  );
